import { ClockIcon } from '@heroicons/react/24/solid';
import Image from 'next/image';
import Link from 'next/link';
import { Button } from '@/components/button';
import { Countdown } from '@/components/Countdown';
import { GameIcon } from '@/components/GameIcon';
import { DateIcon } from '@/components/icons/DateIcon';
import { PrizeIcon } from '@/components/icons/PrizeIcon';
import { hasTournamentPassed } from '@/data/tournaments';
import type { Tournament } from '@/types/tournament';
import { cn } from '@/utils/cn';
import { getTournamentPath } from '@/utils/paths';
import { formatTournamentDate } from '@/utils/tournaments';
import { PastTournamentBadge } from './PastTournamentBadge';

export function TournamentBanner({
  tournament,
  isCtaVisible: isCtaVisibleProp,
  className,
}: {
  tournament: Tournament;
  isCtaVisible?: boolean;
  className?: string;
}) {
  const isDefaultBgDisplayed = tournament.isDefaultBgDisplayed ?? true;
  const isCtaVisible = isCtaVisibleProp ?? true;

  return (
    <div
      className={cn(
        'relative',
        'md:h-[340px]',
        'grid md:grid-cols-[1fr_auto_auto]',
        'overflow-hidden rounded-lg',

        'bg-tournament-banner-gradient',
        className
      )}
    >
      <div className="relative z-2 flex h-full flex-col justify-center px-4 py-6 md:px-15 md:py-0">
        <Link
          className="mb-7"
          href={getTournamentPath({
            slug: tournament.slug,
            game: tournament.game,
          })}
        >
          {tournament.nameImage ? (
            <Image
              alt={`${tournament.name} title`}
              className="max-w-3/4"
              height={80}
              src={tournament.nameImage.url}
              width={408}
            />
          ) : (
            <h1 className="font-bold text-5xl italic">{tournament.name}</h1>
          )}
        </Link>
        <div className="flex items-center gap-3 md:mb-8 md:gap-6">
          <GameIcon className="h-6 w-6" game={tournament.game} />
          {hasTournamentPassed(tournament) ? (
            <PastTournamentBadge />
          ) : (
            <div className="flex items-center gap-2 text-gray">
              <ClockIcon className="w-4 text-blue-gray md:w-5" />
              <Countdown
                expiredElement={
                  <span className="text-red">Betting closed</span>
                }
                suffix="to lock in"
                targetDate={tournament.endDateTime}
              />
            </div>
          )}
        </div>
        {isCtaVisible && !hasTournamentPassed(tournament) && (
          <div className="hidden md:block">
            <Button
              as="link"
              className="max-w-xs"
              href={getTournamentPath({
                slug: tournament.slug,
                game: tournament.game,
              })}
              size="lg"
              variant="primary"
            >
              Make your picks
            </Button>
          </div>
        )}
      </div>
      {tournament.additionalImageRight && (
        <div className="hidden h-full flex-col justify-end pr-15 lg:flex">
          <Image
            alt={`${tournament.name} additional visual`}
            height={tournament.additionalImageRight.height}
            priority
            src={tournament.additionalImageRight.url}
            width={tournament.additionalImageRight.width}
          />
        </div>
      )}
      <div className="z-2 flex flex-row-reverse justify-between px-4 pb-6 md:w-[280px] md:flex-col md:bg-dark-gray/60 md:px-8 md:pt-15 md:pb-8">
        <div className="flex flex-col gap-3">
          <div className="text-center text-gray/50 md:text-left">
            Event info
          </div>
          <div className="flex items-center gap-2">
            <DateIcon className="size-4 text-blue-gray" />
            <div className="text-white">
              <time dateTime={tournament.startDateTime}>
                {formatTournamentDate(tournament.startDateTime)}
              </time>{' '}
              -{' '}
              <time dateTime={tournament.endDateTime}>
                {formatTournamentDate(tournament.endDateTime)}
              </time>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <PrizeIcon className="size-4 text-blue-gray" />
            <div className="text-white">
              <strong className="font-semibold">{tournament.prizePool}</strong>{' '}
              prize pool
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-3 text-center">
          <div className="text-gray/50 text-xs uppercase">Powered by</div>
          <Button
            as="link"
            className="h-auto py-4"
            href={tournament.poweredBy.url}
            size="lg"
            variant="outlineWithBg"
          >
            {tournament.poweredBy.icon && (
              <Image
                alt={`${tournament.poweredBy.name} icon`}
                className="h-8 w-8"
                height={tournament.poweredBy.icon.height}
                src={tournament.poweredBy.icon.url}
                width={tournament.poweredBy.icon.width}
              />
            )}
            {tournament.poweredBy.name}
          </Button>
        </div>
      </div>
      {isDefaultBgDisplayed && <DefaultBg />}
    </div>
  );
}

function DefaultBg() {
  return (
    <div className="absolute inset-0 z-1 bg-[url('/tournaments/tournaments_banner.webp')] md:bg-position-[0_-20px] md:bg-size-[120%]" />
  );
}
